//  Third-party
import uniq from 'lodash/uniq';

// Constants and types
import Dictionary from '@/types/dictionary';
import Stock from '@/types/stock';
import Benchmark from '@/types/benchmark';
import ComparisonItem from '@/types/comparison-item';
import BasicBenchmark from '@/types/basic-benchmark';
import Risk from '@/types/risk';
import Comparison from '@/types/comparison';

// Utils
import { addCommonInceptionToPerformance, benchmarkToBasicBenchmark } from './shared';
import { COMPARISON_ITEM_TYPES, Interval } from '@/utils/constants';
import { HOLDING_TYPES } from '@/utils/portfolios';
import disclaimers from '@/utils/disclaimers';
import { convertShelvesToShelfStatuses } from '@/utils/shelf-management';
import { generateLocalizedString, LOCALE } from '@/utils/i18n';
import { formatCustomAllocationData, getStockAllocations, stockToInvestmentItem } from '@/utils/stocks';
import { deepClone } from '@/utils/objects';
import { setCommonInceptionRiskMetrics } from '@/utils/common-inception';
import { calculateRiskMetrics } from '@/utils/calculation-utils';
import { riskRatingLiteralToAllocations } from '@/utils/risk';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import { DETAILED_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import AppRegion from '@/types/app-region';
import { createOtherSubAllocation } from '@/utils/allocations';

const stockCommonInceptionRiskMetrics = (stock: Stock, benchmarks?: Benchmark[], comparison?: Comparison) => {
  const clone = deepClone(stock);
  const { performance, benchmarkId, riskFreeId } = clone;
  const marketBenchmark = benchmarks && benchmarks.find(b => b._id === benchmarkId);
  const riskFreeBenchmark = benchmarks && benchmarks.find(b => b._id === riskFreeId);

  // TODO CONN-4280 -- Confirm logic for risk metrics, CURRENCY.USD shouldn't map rollingUSD
  // const useBaseCurrency = !!(currency === CURRENCY.USD);
  // const rollingBase = useBaseCurrency ? 'rollingUSD' || 'rolling' : 'rolling';

  const marketRolling = (marketBenchmark && marketBenchmark.performance.rolling[Interval.Monthly]) || [];
  const riskFreeRolling = (riskFreeBenchmark && riskFreeBenchmark.performance.rolling[Interval.Monthly]) || [];

  // performance calculation
  const pRolling = deepClone(performance.rolling[Interval.Monthly]);
  const pRiskMetrics = calculateRiskMetrics(pRolling, marketRolling, riskFreeRolling, true, comparison?.commonInception);
  clone.performance = {
    ...clone.performance,
    ...setCommonInceptionRiskMetrics(clone.performance, pRiskMetrics),
  };

  // performanceBase calculation
  if (clone.performanceBase) {
    const pbRolling = deepClone(clone.performanceBase.rolling[Interval.Monthly]);
    const pbRiskMetrics = calculateRiskMetrics(pbRolling, marketRolling, riskFreeRolling, true, comparison?.commonInception);
    clone.performanceBase = {
      ...clone.performanceBase,
      ...setCommonInceptionRiskMetrics(clone.performanceBase, pbRiskMetrics),
    };
  }

  return clone;
};

/**
 * Generates a comparison item from a single stock.
 *
 * @param {Stock} stock Stock model object.
 * @return {ComparisonItem} Comparison item based on the fund.
 */
const stockToComparisonItem = (
  stock: Stock,
  benchmarks?: Benchmark[],
  options?: {
    itemFlags?: string[];
    comparison?: Comparison;
  },
  appRegion: AppRegion = AppRegion.CAN,
): ComparisonItem => {
  const riskFreeBenchmarks = benchmarks
    ? benchmarks.reduce((acc, curr) => {
        if (curr.lipperId === stock.riskFreeId) {
          acc[stock._id] = benchmarkToBasicBenchmark(curr);
        }
        return acc;
      }, {} as Dictionary<BasicBenchmark>)
    : undefined;

  const marketBenchmarks = benchmarks
    ? benchmarks.reduce((acc, curr) => {
        if (curr.lipperId === stock.benchmarkId) {
          acc[stock._id] = benchmarkToBasicBenchmark(curr);
        }
        return acc;
      }, {} as Dictionary<BasicBenchmark>)
    : undefined;

  stock = {
    ...stock,
    ...stockCommonInceptionRiskMetrics(stock, benchmarks, options?.comparison),
  };

  const risk: Risk = {
    standardDeviation: stock.performance.standardDeviation,
    maximumDrawdown: stock.performance.maximumDrawdown,
    correlationCoefficient: stock.performance.correlationCoefficient,
    rSquared: stock.performance.rSquared,
    downside: stock.performance.downside,
    infoRatio: stock.performance.infoRatio,
    trackingError: stock.performance.trackingError,
    upside: stock.performance.upside,
    alpha: stock.performance.alpha,
    beta: stock.performance.beta,
    sharpeRatio: stock.performance.sharpeRatio,
    sortino: stock.performance.sortino,
  };

  const performance = addCommonInceptionToPerformance(stock.performance, options?.comparison?.commonInception?.startTimestamp);

  const performanceBase = addCommonInceptionToPerformance(stock.performanceBase || stock.performance, options?.comparison?.commonInception?.startTimestamp);
  const stockInvestmentItem = stockToInvestmentItem(stock);
  const flags = options?.itemFlags || stock.flags;
  let detailedAsset = formatCustomAllocationData(stock.customData?.allocations?.asset?.data as LocalizedAllocationDetails[]);

  if (!detailedAsset?.length && appRegion === AppRegion.US) {
    detailedAsset = [
      {
        name: generateLocalizedString(`Comparison.Table.AssetDetails.${DETAILED_ASSET_ALLOCATION.EQ}`),
        weight: 100,
        subAllocations: [createOtherSubAllocation()],
      },
    ];
  }

  return {
    _id: stock._id,
    type: COMPARISON_ITEM_TYPES.STOCK,
    name: {
      [LOCALE.EN_CA]: stock.companyName,
      [LOCALE.FR_CA]: stock.companyName,
    },
    holdings: [
      {
        id: stock._id,
        type: HOLDING_TYPES.STOCK,
        symbol: stock.ticker,
        name: {
          [LOCALE.EN_CA]: stock.companyName,
          [LOCALE.FR_CA]: stock.companyName,
        },
        weight: 100,
        rolling: performance && performance.rolling,
        baseRolling: performanceBase && performanceBase.rolling,
        nav: stock.nav,
        distribution: stock.distribution,
      },
    ],
    performance,
    performanceBase,
    originalRisk: { ...risk },
    complianceComparisons: stock.complianceComparisons,
    allocations: {
      asset: getStockAllocations(stock),
      detailedAsset,
      country: [
        {
          name: stock.domicile,
          weight: 100,
        },
      ],
      sector: [
        {
          name: stock.industry,
          weight: 100,
        },
      ],
      equityCountry: [
        {
          name: stock.domicile,
          weight: 100,
        },
      ],
    },
    detailedHoldings: {
      list: [{ name: stock.companyName, weight: 100 }],
    },
    currency: stock.currency,
    domicile: stock.domicile,
    disclaimers: disclaimers.getComparisonStockDisclaimers(stock),
    dates: stock.dates,
    benchmarks: {
      market: marketBenchmarks && Object.keys(marketBenchmarks).length ? marketBenchmarks : undefined,
      riskFree: riskFreeBenchmarks && Object.keys(riskFreeBenchmarks).length ? riskFreeBenchmarks : undefined,
    },
    characteristics: {
      returnOnEquity: stock.returnOnEquity,
    },
    investmentInfo: {
      pbRatio: stockInvestmentItem.pbRatio,
      peRatio: stockInvestmentItem.peRatio,
      distributionYield: stockInvestmentItem.distributionYield,
      returnOnEquity: stockInvestmentItem.returnOnEquity,
      capitalization: stockInvestmentItem.capitalization,
    },
    esg: stock.esg,
    riskRating: stock.riskRating ? riskRatingLiteralToAllocations(stock.riskRating) : undefined,
    flags: uniq([...(flags || []), ...convertShelvesToShelfStatuses(stock.filteredShelves, stock.complianceComparisons)]),
  };
};

export default stockToComparisonItem;
